import { createButton } from "@gluestack-ui/core/button/creator";
import { PrimitiveIcon, UIIcon } from "@gluestack-ui/core/icon/creator";
import { VariantProps, tva, withStyleContext } from "@gluestack-ui/utils";
import { cssInterop } from "nativewind";
import React from "react";
import { Pressable, Text, View } from "react-native";
import { Spinner } from "../Spinner/Spinner";

const SCOPE = "NAVIGATION_ACTION";

const Root = withStyleContext(Pressable, SCOPE);

const UIButton = createButton({
	Root: Root,
	Text,
	Group: View,
	Spinner: Spinner,
	Icon: UIIcon,
});

cssInterop(PrimitiveIcon, {
	className: {
		target: "style",
		nativeStyleToProp: {
			height: true,
			width: true,
			fill: true,
			color: "classNameColor",
			stroke: true,
		} as any,
	},
});

const buttonStyle = tva({
	base: "flex-row items-center justify-center data-[focus-visible=true]:web:outline-none data-[focus-visible=true]:web:ring-2 data-[disabled=true]:opacity-40 gap-2 data-[active=true]:opacity-50",
});

const buttonTextStyle = tva({
	base: "web:select-none text-sm text-neutral-950 font-sans-semibold web:font-semibold",
});

const buttonIconStyle = tva({
	base: "fill-none h-4 w-4 text-neutral-950",
});

export type INavigationActionProps = Omit<React.ComponentPropsWithoutRef<typeof UIButton>, "context"> &
	VariantProps<typeof buttonStyle> & { className?: string };

const NavigationAction = React.forwardRef<React.ComponentRef<typeof UIButton>, INavigationActionProps>(
	({ className, ...props }, ref) => {
		return <UIButton ref={ref} {...props} className={buttonStyle({ class: className })} />;
	},
);

type IButtonTextProps = React.ComponentPropsWithoutRef<typeof UIButton.Text> &
	VariantProps<typeof buttonTextStyle> & { className?: string };

const NavigationActionText = React.forwardRef<React.ComponentRef<typeof UIButton.Text>, IButtonTextProps>(
	({ className, ...props }, ref) => {
		return (
			<UIButton.Text
				ref={ref}
				{...props}
				className={buttonTextStyle({
					class: className,
				})}
			/>
		);
	},
);

type INavigationActionIcon = React.ComponentPropsWithoutRef<typeof UIButton.Icon> &
	VariantProps<typeof buttonIconStyle> & {
		className?: string | undefined;
		as?: React.ElementType;
	};

const NavigationActionIcon = React.forwardRef<React.ComponentRef<typeof UIButton.Icon>, INavigationActionIcon>(
	({ className, ...props }, ref) => {
		return (
			<UIButton.Icon
				{...props}
				className={buttonIconStyle({
					class: className,
				})}
				ref={ref}
			/>
		);
	},
);

NavigationAction.displayName = "NavigationAction";
NavigationActionText.displayName = "NavigationActionText";
NavigationActionIcon.displayName = "NavigationActionIcon";

export { NavigationAction, NavigationActionIcon, NavigationActionText };
